import React, { useState } from 'react';
import { FileText, Download, Languages, Globe } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, NoteCard } from '../shared';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const AITranslator = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [translatedText, setTranslatedText] = useState('');
    const [targetLanguage, setTargetLanguage] = useState('es'); // Default: Spanish

    const languages = [
        { code: 'es', name: 'Spanish', flag: '🇪🇸' },
        { code: 'fr', name: 'French', flag: '🇫🇷' },
        { code: 'de', name: 'German', flag: '🇩🇪' },
        { code: 'it', name: 'Italian', flag: '🇮🇹' },
        { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
        { code: 'ru', name: 'Russian', flag: '🇷🇺' },
        { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
        { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
        { code: 'ko', name: 'Korean', flag: '🇰🇷' },
        { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
        { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
        { code: 'tr', name: 'Turkish', flag: '🇹🇷' }
    ];

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);
            setTranslatedText('');
            toast.success('PDF loaded successfully');
        }
    };

    const extractTextFromPDF = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;

        let fullText = '';
        const totalPages = pdf.numPages;

        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            fullText += pageText + '\\n\\n';

            setProgress(Math.round((pageNum / totalPages) * 40));
        }

        return fullText;
    };

    const translatePDF = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(5);
            setTranslatedText('');

            // Extract text from PDF
            const extractedText = await extractTextFromPDF(file);

            if (!extractedText.trim()) {
                throw new Error('No text found in PDF. The PDF might be scanned or image-based.');
            }

            setProgress(50);

            // Translate text using AI
            const selectedLang = languages.find(l => l.code === targetLanguage);
            const translated = await translateText(extractedText, targetLanguage);

            setProgress(90);
            setTranslatedText(translated);

            setProgress(100);
            toast.success(`Translated to ${selectedLang.name} successfully!`);

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Translation error:', error);
            toast.error(`Failed to translate PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    const translateText = async (text, targetLang) => {
        // Mock translation - Replace with actual AI translation API
        await new Promise(resolve => setTimeout(resolve, 2000));

        const selectedLang = languages.find(l => l.code === targetLang);

        // Mock translated text
        return `[Translated to ${selectedLang.name}]\\n\\n${text.substring(0, 500)}...\\n\\n(This is a mock translation. In production, this would be translated using an AI translation API like Google Translate, DeepL, or Cloudflare AI.)`;
    };

    const downloadTranslatedPDF = async () => {
        try {
            // Create new PDF with translated text
            const pdfDoc = await PDFDocument.create();
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

            const pageWidth = 595.28; // A4 width in points
            const pageHeight = 841.89; // A4 height in points
            const margin = 50;
            const fontSize = 12;
            const lineHeight = fontSize * 1.5;
            const maxWidth = pageWidth - (margin * 2);

            // Split text into lines that fit the page width
            const words = translatedText.split(' ');
            const lines = [];
            let currentLine = '';

            for (const word of words) {
                const testLine = currentLine + word + ' ';
                const testWidth = font.widthOfTextAtSize(testLine, fontSize);

                if (testWidth > maxWidth && currentLine !== '') {
                    lines.push(currentLine.trim());
                    currentLine = word + ' ';
                } else {
                    currentLine = testLine;
                }
            }
            if (currentLine) lines.push(currentLine.trim());

            // Add pages and text
            let page = pdfDoc.addPage([pageWidth, pageHeight]);
            let yPosition = pageHeight - margin;

            for (const line of lines) {
                if (yPosition < margin) {
                    page = pdfDoc.addPage([pageWidth, pageHeight]);
                    yPosition = pageHeight - margin;
                }

                page.drawText(line, {
                    x: margin,
                    y: yPosition,
                    size: fontSize,
                    font: font,
                    color: rgb(0, 0, 0)
                });

                yPosition -= lineHeight;
            }

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            const fileName = file.name.replace(/\\.pdf$/i, '');
            const selectedLang = languages.find(l => l.code === targetLanguage);
            saveAs(blob, `${fileName}_${selectedLang.code}.pdf`);

            toast.success('Translated PDF downloaded!');
        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error(`Failed to generate PDF: ${error.message}`);
        }
    };

    const downloadAsText = () => {
        const blob = new Blob([translatedText], { type: 'text/plain' });
        const fileName = file.name.replace(/\\.pdf$/i, '');
        const selectedLang = languages.find(l => l.code === targetLanguage);
        saveAs(blob, `${fileName}_${selectedLang.code}.txt`);
        toast.success('Translation exported as text!');
    };

    return (
        <div>
            <div className="tool-header">
                <div className="tool-icon-wrapper">
                    <Languages size={40} color="var(--color-text-primary)" />
                </div>
                <div className="tool-header-content">
                    <h1 className="tool-title">AI Translator</h1>
                    <p className="tool-description">
                        Translate PDF documents to different languages using AI. Supports 50+ languages.
                    </p>
                </div>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setTranslatedText(''); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && !translatedText && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                <Globe size={20} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                                Select Target Language
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 'var(--spacing-3)' }}>
                                {languages.map(lang => (
                                    <div
                                        key={lang.code}
                                        onClick={() => setTargetLanguage(lang.code)}
                                        style={{
                                            padding: 'var(--spacing-3)',
                                            border: `2px solid ${targetLanguage === lang.code ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                            borderRadius: 'var(--radius-md)',
                                            cursor: 'pointer',
                                            backgroundColor: targetLanguage === lang.code ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
                                            transition: 'all 0.2s',
                                            textAlign: 'center'
                                        }}
                                    >
                                        <div style={{ fontSize: 'var(--text-2xl)', marginBottom: 'var(--spacing-1)' }}>{lang.flag}</div>
                                        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>{lang.name}</div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'var(--spacing-2)' }}>
                                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>Translating...</span>
                                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>{Math.round(progress)}%</span>
                            </div>
                            <ProgressBar value={progress} striped />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button variant="primary" size="lg" onClick={translatePDF} disabled={isProcessing} loading={isProcessing} fullWidth>
                            <Languages size={20} />
                            Translate to {languages.find(l => l.code === targetLanguage)?.name}
                        </Button>
                    </div>
                </>
            )}

            {translatedText && (
                <div style={{ marginTop: 'var(--spacing-6)' }}>
                    <Card>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)' }}>
                                Translation Preview
                            </h3>
                            <Badge variant="success">
                                {languages.find(l => l.code === targetLanguage)?.flag} {languages.find(l => l.code === targetLanguage)?.name}
                            </Badge>
                        </div>

                        <div style={{
                            padding: 'var(--spacing-4)',
                            backgroundColor: 'var(--color-surface-elevated)',
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--spacing-4)',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            lineHeight: 'var(--leading-relaxed)',
                            whiteSpace: 'pre-wrap'
                        }}>
                            {translatedText}
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
                            <Button variant="primary" onClick={downloadTranslatedPDF} style={{ flex: 1 }}>
                                <Download size={20} />
                                Download as PDF
                            </Button>
                            <Button variant="secondary" onClick={downloadAsText} style={{ flex: 1 }}>
                                <Download size={20} />
                                Download as Text
                            </Button>
                            <Button variant="ghost" onClick={() => setTranslatedText('')} style={{ flex: 1 }}>
                                Translate Again
                            </Button>
                        </div>
                    </Card>
                </div>
            )}

            <NoteCard variant="info">
                <strong>Note:</strong> This tool uses AI to translate PDF content. Translation quality may vary depending on the source language and document complexity.
            </NoteCard>
        </div>
    );
};

export default AITranslator;
